import React from 'react'
import { Gauge, TrendingDown, TrendingUp, XOctagon } from 'lucide-react'
import DashboardCard from '@/components/dashboard/DashboardCard'
import { cn } from '@/lib/utils'
import { formatCount } from '@/lib/format'

/**
 * Band 5, card B — rate health. How much of the next month's pricing is welded to a
 * guardrail rather than being set by demand.
 *
 * WHY EVERY COUNT CARRIES ITS DENOMINATOR. "148 at floor" is unreadable: it is alarming
 * on a small property and unremarkable on a large one. Every figure here is printed as
 * "148 of 840 · 17.6%", and both halves come from the backend — the count, the total and
 * the share are all computed in SQL (CLAUDE.md, "Frontend: display only, zero math").
 */

export interface RateHealthProps {
  atFloor: number
  atFloorPct: number
  atCeiling: number
  atCeilingPct: number
  stopSell: number
  stopSellPct: number
  /** Rates assessed in the window — the denominator behind every share above. */
  total: number
  windowDays: number
  startDate: string
  endDate: string
}

/**
 * The share at which "some nights hit the floor" becomes "the floor is doing the pricing".
 * A fifth of a whole month's rates pinned to the minimum is a standing condition, not a
 * quiet weekend, and it is worth naming in words rather than leaving as a percentage.
 */
const AT_FLOOR_NOTICE_PCT = 20

interface HealthRowProps {
  icon: React.ElementType
  label: string
  description: string
  count: number
  pct: number
  total: number
  color: string
  bgColor: string
}

function HealthRow({
  icon: Icon,
  label,
  description,
  count,
  pct,
  total,
  color,
  bgColor,
}: HealthRowProps) {
  return (
    <div className="flex items-center gap-3 rounded-lg p-3 transition-colors hover:bg-muted/30">
      <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', bgColor)}>
        <Icon className={cn('h-4 w-4', color)} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <div className="shrink-0 text-right">
        <p className={cn('text-lg font-bold tabular-nums', count > 0 ? color : 'text-muted-foreground')}>
          {formatCount(count)}
          <span className="ml-1 text-xs font-medium text-muted-foreground">
            of {formatCount(total)}
          </span>
        </p>
        <p className="text-[11px] text-muted-foreground tabular-nums">{pct}%</p>
      </div>
    </div>
  )
}

function formatDay(iso: string): string {
  const parsed = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) return iso
  return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

export default function RateHealthCard({
  atFloor,
  atFloorPct,
  atCeiling,
  atCeilingPct,
  stopSell,
  stopSellPct,
  total,
  windowDays,
  startDate,
  endDate,
}: RateHealthProps) {
  const floorIsPricing = atFloorPct >= AT_FLOOR_NOTICE_PCT

  return (
    <DashboardCard
      title="Rate health"
      description={
        total > 0 ? (
          <>
            {formatCount(total)} rates across the next {windowDays} nights ({formatDay(startDate)} –{' '}
            {formatDay(endDate)})
          </>
        ) : (
          <>Next {windowDays} nights</>
        )
      }
      icon={<Gauge className="h-4 w-4 shrink-0 text-muted-foreground" />}
      help={{
        content:
          'How many of the next month’s rates are pinned to a guardrail. A rate sitting at its floor or ceiling has stopped responding to demand — the bound is setting the price, not the engine.',
        learnMore: '/guides/price-guardrails',
      }}
      padded={total === 0}
      bodyClassName={total === 0 ? 'p-5' : 'divide-y divide-border/30 p-2'}
      footer={
        floorIsPricing ? (
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            <span className="font-semibold text-amber-700">
              {atFloorPct}% of the next {windowDays} nights' rates are at their floor.
            </span>{' '}
            On those nights the guardrail is setting the price, not demand — the engine wanted to
            go lower and could not. Raise the minimum only if you mean to hold that price; lower it
            if you want the engine to compete.
          </p>
        ) : undefined
      }
    >
      {total === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm">
          <Gauge className="mx-auto h-8 w-8 text-muted-foreground/40" />
          <p className="mt-3 text-sm font-medium text-foreground">No rates for the next {windowDays} nights</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Guardrail health appears once the engine has priced these nights.
          </p>
        </div>
      ) : (
        <>
          <HealthRow
            icon={TrendingDown}
            label="At floor"
            description="Pinned to the room's minimum rate"
            count={atFloor}
            pct={atFloorPct}
            total={total}
            color="text-amber-600"
            bgColor="bg-amber-50"
          />
          <HealthRow
            icon={TrendingUp}
            label="At ceiling"
            description="Pinned to the room's maximum rate"
            count={atCeiling}
            pct={atCeilingPct}
            total={total}
            color="text-rose-600"
            bgColor="bg-rose-50"
          />
          <HealthRow
            icon={XOctagon}
            label="Stop sell"
            description="Closed — not offered for sale"
            count={stopSell}
            pct={stopSellPct}
            total={total}
            color="text-slate-600"
            bgColor="bg-slate-100"
          />
        </>
      )}
    </DashboardCard>
  )
}
